"use client";

import Link from "next/link";
import { ChevronDown, CreditCard, Gift, Lock, RotateCcw, Sparkles, Truck } from "lucide-react";
import { useEffect, useId, useRef, useState, useSyncExternalStore, type MouseEvent } from "react";
import { checkoutHref } from "@/dropai/checkout-link";
import type { ProductVariant, StoreProduct } from "@/dropai/types";
import { store } from "@/lib/store";
import type { MediaSlot, ThemeContent } from "../types";
import { addToCart } from "./cart";
import { deliverySentence, deliveryWindow, spainNow } from "./delivery";
import { SlotPhoto, Stars } from "./parts";
import { BADGE, euros, FOCUS, PENDING, PRIMARY } from "./ui";

type Selection = { pack: number; options: Record<string, string>; missing: string | null };

const product: StoreProduct = store.product;
const variants: readonly ProductVariant[] = product.variants ?? [];
let state: Selection = { pack: -1, options: {}, missing: null };
const listeners = new Set<() => void>();

function setState(next: Partial<Selection>) {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const noSubscription = () => () => {};

function useSelection(content: ThemeContent): Selection {
  const sales = content.sales!;
  const current = useSyncExternalStore(subscribe, () => state, () => state);
  // Por defecto: el pack recomendado y la primera opción de cada variante salvo la talla, que nunca se preselecciona (E07).
  // Por defecto, 1 unidad (el pack de 2 lleva «Recomendado» pero NO sale preseleccionado: «LO QUIERO» no mete 2 sin que se elijan).
  const recommended = Math.max(0, sales.packs.findIndex((pack) => pack.units === 1));
  const options: Record<string, string> = {};
  for (const variant of variants) {
    if (variant.label === sales.sizeLabel) continue;
    if (variant.options.length > 0) options[variant.label] = variant.options[0];
  }
  return { pack: current.pack === -1 ? recommended : current.pack, options: { ...options, ...current.options }, missing: current.missing };
}

function unitsOf(content: ThemeContent, selection: Selection): { price: number; units: number } {
  const pack = content.sales!.packs[selection.pack];
  return pack ? { price: pack.price, units: pack.units } : { price: store.product.price, units: 1 };
}

/**
 * El botón de compra: añade lo elegido al carrito lateral (7.5). Sin JS lleva directo al pago; sin
 * talla elegida, baja al selector y lo marca (C21).
 */
export function useBuyAction(content: ThemeContent) {
  const sales = content.sales!;
  const selection = useSelection(content);
  const sizeMissing = sales.sizeLabel !== undefined && !selection.options[sales.sizeLabel];
  const { price, units } = unitsOf(content, selection);
  const label = sizeMissing ? (sales.sizeCta ?? sales.cta) : sales.packs.length > 0 ? sales.cta + " · " + euros(price) : sales.cta;
  const base = checkoutHref(variants, selection.options);
  const link = units > 1 ? base + (base.includes("?") ? "&" : "?") + "unidades=" + String(units) : base;
  function onClick(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    if (sizeMissing) {
      setState({ missing: sales.sizeLabel ?? null });
      const box = document.getElementById("elige-talla");
      if (box === null) {
        window.location.href = "/producto/" + sales.slug + "#elige-talla";
        return;
      }
      const calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      box.scrollIntoView({ block: "center", behavior: calm ? "auto" : "smooth" });
      box.querySelector("input")?.focus({ preventScroll: true });
      return;
    }
    addToCart({ options: selection.options, units });
  }
  return { label, link, onClick, price, units };
}

/** E01 + v2 5.3: galería 1:1 a sangre con contador «1/7»; en ordenador, miniaturas a la izquierda. */
function Gallery({ content }: { content: ThemeContent }) {
  const sales = content.sales!;
  // Sin fotos (G-005) la ficha no reserva un cuadrado vacío: la caja de compra ocupa el sitio (ProductBuy).
  const items: MediaSlot[] = (content.media?.gallery ?? []).filter((item) => item.photo !== undefined);
  const track = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  useEffect(() => {
    const node = track.current;
    if (!node) return;
    const onScroll = () => setActive(Math.round(node.scrollLeft / Math.max(1, node.clientWidth)));
    node.addEventListener("scroll", onScroll, { passive: true });
    return () => node.removeEventListener("scroll", onScroll);
  }, []);
  function go(index: number) {
    const node = track.current;
    const calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (node) node.scrollTo({ left: index * node.clientWidth, behavior: calm ? "auto" : "smooth" });
  }
  return (
    <div className="lg:sticky lg:top-24 lg:grid lg:grid-cols-[4.5rem_minmax(0,1fr)] lg:gap-4">
      <div className="hidden lg:flex lg:flex-col lg:gap-3">
        {items.map((item, index) => (
          <button key={index} type="button" onClick={() => go(index)} aria-label={"Ver foto " + String(index + 1)} aria-current={index === active ? "true" : undefined} className={"relative size-[4.5rem] overflow-hidden rounded-[14px] bg-(--wl-photo-bg) transition-[box-shadow,transform] duration-200 " + (index === active ? "ring-2 ring-(--wl-accent) ring-offset-2 ring-offset-(--wl-surface)" : "opacity-80 hover:opacity-100") + " " + FOCUS}>
            <SlotPhoto slot={item} sizes="72px" compact />
          </button>
        ))}
      </div>
      <div className="relative min-w-0">
        <div ref={track} className="flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] lg:rounded-[24px] [&::-webkit-scrollbar]:hidden">
          {items.map((item, index) => (
            <div key={index} className="relative aspect-square w-full shrink-0 snap-center overflow-hidden bg-(--wl-photo-bg)">
              <SlotPhoto slot={item} sizes="(min-width: 64rem) 50vw, 100vw" priority={index === 0} labelPosition="bottom-3 left-3" />
            </div>
          ))}
        </div>
        <span className={BADGE + " pointer-events-none absolute top-3 left-3 shadow-(--wl-shadow-accent)"}>
          <span aria-hidden="true">{sales.heroBadge.emoji}</span>
          {sales.heroBadge.text}
        </span>
        {items.length > 1 ? (
          <span className="wl-glass pointer-events-none absolute right-3 bottom-3 rounded-full px-3 py-1 text-xs font-bold text-(--wl-ink) tabular-nums">{String(active + 1) + "/" + String(items.length)}</span>
        ) : null}
      </div>
    </div>
  );
}

/** E10: «Pide hoy y llega entre el vie 30 oct y el mié 4 nov», calculado el día de la visita. */
function DeliveryLine({ content }: { content: ThemeContent }) {
  const sales = content.sales!;
  const mounted = useSyncExternalStore(noSubscription, () => true, () => false);
  const days = sales.shippingDays;
  let body = <span className="text-(--wl-ink-muted)">{sales.deliveryFallback}</span>;
  if (days) {
    if (mounted) {
      const sentence = deliverySentence(deliveryWindow(spainNow(), days[0], days[1]));
      body = (
        <span>
          {sentence.lead + " "}
          <strong className="font-bold">{sentence.from}</strong>
          {sentence.to ? <>{" y el "}<strong className="font-bold">{sentence.to}</strong></> : null}
        </span>
      );
    } else {
      body = <span>{"Llega en " + String(days[0]) + "-" + String(days[1]) + " días"}</span>;
    }
  }
  return (
    <p className="mt-4 flex min-h-6 items-start gap-2 text-sm font-medium">
      <span aria-hidden="true" className="relative mt-1.5 flex size-2.5 shrink-0">
        <span className="wl-pulse absolute inset-0 rounded-full bg-(--wl-success) opacity-60" />
        <span className="relative size-2.5 rounded-full bg-(--wl-success)" />
      </span>
      {body}
    </p>
  );
}

/** El precio: con oferta, el tachado y el porcentaje; sin ella, el ahorro del pack o el dato del lado (8.2). */
function PriceRow({ content, price, units }: { content: ThemeContent; price: number; units: number }) {
  const sales = content.sales!;
  const pack = sales.packs.find((item) => item.units === units);
  const before = sales.offer ? Math.round(sales.offer.compareAtPrice * units * 100) / 100 : null;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="font-display text-[2rem] leading-none tabular-nums lg:text-[2.5rem] [font-weight:var(--wl-display-weight)]">{euros(price)}</span>
        {before !== null ? <s className="text-lg text-(--wl-ink-muted) tabular-nums">{euros(before)}</s> : null}
        {sales.offer ? (
          <span className={BADGE}>{"-" + String(sales.offer.percent) + " %"}</span>
        ) : pack && pack.saving > 0 ? (
          <span className={BADGE}>{"Ahorras " + euros(pack.saving)}</span>
        ) : null}
      </div>
      <p className="mt-2 text-[0.8125rem] text-(--wl-ink-muted)">
        {pack && pack.units > 1 ? euros(pack.perUnit) + "/ud. · " : ""}
        {sales.priceNote}
        {sales.offer ? " · Precio tachado: el más bajo de los 30 días anteriores a la oferta." + (sales.offer.demo ? " (DEMO)" : "") : ""}
      </p>
    </div>
  );
}

function Rating({ content }: { content: ThemeContent }) {
  const sales = content.sales!;
  if (sales.proof) {
    return (
      <a href="#opiniones" className={"inline-flex min-h-11 items-center gap-2 text-sm font-semibold " + FOCUS}>
        <Stars rating={sales.proof.rating} />
        <span className="tabular-nums">{sales.proof.rating.toFixed(1).replace(".", ",") + " · " + String(sales.proof.count) + " opiniones"}</span>
      </a>
    );
  }
  return (
    <p className="inline-flex min-h-8 items-center gap-2 rounded-full bg-(--wl-accent-soft) px-3 text-sm font-bold">
      {/env[ií]o/i.test(sales.priceBadge) ? <Truck aria-hidden="true" className="size-4 text-(--wl-accent-text)" /> : /regalo/i.test(sales.priceBadge) ? <Gift aria-hidden="true" className="size-4 text-(--wl-accent-text)" /> : <Sparkles aria-hidden="true" className="size-4 text-(--wl-accent-text)" />}
      {sales.priceBadge}
    </p>
  );
}

/** Variantes y packs: lo que se elige antes del botón. */
function Choices({ content, compact = false }: { content: ThemeContent; compact?: boolean }) {
  const sales = content.sales!;
  const selection = useSelection(content);
  const baseId = useId();
  return (
    <>
      {variants.map((variant, index) => {
        const isSize = variant.label === sales.sizeLabel;
        const chosen = selection.options[variant.label];
        return (
          <fieldset key={variant.label} id={isSize && !compact ? "elige-talla" : undefined} className="mt-6 min-w-0 scroll-mt-28">
            <legend className="text-sm font-semibold">
              {variant.label}
              {chosen ? <span className="font-normal text-(--wl-ink-muted)">{": " + chosen}</span> : null}
            </legend>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {variant.options.map((option, optionIndex) => {
                const inputId = baseId + "-" + String(index) + "-" + String(optionIndex);
                return (
                  <span key={option}>
                    <input id={inputId} type="radio" name={baseId + "-" + String(index)} value={option} checked={chosen === option} onChange={() => setState({ options: { ...state.options, [variant.label]: option }, missing: null })} className="peer sr-only" />
                    <label htmlFor={inputId} className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-full border border-(--wl-line) bg-(--wl-surface) px-4 font-semibold transition-[transform,box-shadow] duration-200 active:scale-95 peer-checked:border-transparent peer-checked:bg-(--wl-accent-soft) peer-checked:shadow-[0_0_0_2px_var(--wl-accent)] peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-(--wl-ink)">
                      {option}
                    </label>
                  </span>
                );
              })}
            </div>
            {isSize && selection.missing === variant.label ? (
              <p role="alert" className="mt-2 text-sm font-semibold text-(--wl-urgency)">Elige tu talla antes de seguir.</p>
            ) : null}
          </fieldset>
        );
      })}
      {sales.packs.length > 0 ? (
        <fieldset className="mt-6 grid gap-3">
          <legend className="mb-3 text-sm font-semibold">Elige tu pack</legend>
          {sales.packs.map((item, index) => {
            const inputId = baseId + "-pack-" + String(index);
            const selected = index === selection.pack;
            return (
              <div key={item.units} className="relative">
                <input id={inputId} type="radio" name={baseId + "-pack"} checked={selected} onChange={() => setState({ pack: index })} className="peer sr-only" />
                <label htmlFor={inputId} className={"flex min-h-[4.5rem] cursor-pointer items-center gap-3 rounded-[18px] px-4 py-3 transition-[box-shadow,transform] duration-300 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-(--wl-ink) " + (selected ? "wl-ring wl-pack-on wl-pop bg-(--wl-surface)" : "border border-(--wl-line) bg-(--wl-surface) hover:shadow-(--wl-shadow-soft)")}>
                  <span aria-hidden="true" className={"flex size-6 shrink-0 items-center justify-center rounded-full " + (selected ? "wl-grad-accent" : "border-2 border-(--wl-line)")}>
                    {selected ? <span className="size-2.5 rounded-full bg-current" /> : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-bold">{item.units === 1 ? "1 unidad" : String(item.units) + " unidades"}</span>
                    <span className="block text-sm text-(--wl-ink-muted)">{euros(item.perUnit) + "/ud."}</span>
                    {item.gift ? (
                      <span className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-(--wl-accent-text)"><Gift aria-hidden="true" className="size-3.5" />{"+ " + item.gift + " de regalo"}</span>
                    ) : null}
                  </span>
                  <span className="text-right">
                    <span className="block text-lg font-extrabold tabular-nums">{euros(item.price)}</span>
                    {item.saving > 0 ? <span className="mt-1 inline-block rounded-full bg-(--wl-accent-soft) px-2 py-0.5 text-xs font-bold">{"Ahorras " + euros(item.saving)}</span> : null}
                  </span>
                </label>
                {item.recommended ? (
                  <span className={BADGE + " absolute -top-3 right-4 px-2.5 py-0.5 text-[0.6875rem]"}>Recomendado</span>
                ) : null}
              </div>
            );
          })}
        </fieldset>
      ) : null}
    </>
  );
}

const SEAL_ICONS = { truck: Truck, return: RotateCcw, lock: Lock };

/** Lo que acompaña a la compra en la ficha de CUALQUIER producto: sellos, medios de pago y preguntas de servicio. */
function Reassurance({ content, extra = false }: { content: ThemeContent; extra?: boolean }) {
  const sales = content.sales!;
  return (
    <>
      <ul className="mt-5 grid grid-cols-3 gap-2 text-center text-[0.75rem] font-semibold">
        {sales.seals.map((seal) => {
          const Icon = SEAL_ICONS[seal.icon];
          return (
            <li key={seal.text}>
              <a href={seal.href} className={"flex h-full min-h-11 flex-col items-center gap-1.5 rounded-[14px] bg-(--wl-accent-soft) px-2 py-3 " + FOCUS}>
                <Icon aria-hidden="true" className="size-5 text-(--wl-accent-text)" />
                {seal.text}
              </a>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 flex items-center gap-1.5 text-sm text-(--wl-ink-muted)">
        <CreditCard aria-hidden="true" className="size-4" />
        {sales.payments}
      </p>
      <div className="mt-6 border-t border-(--wl-line)">
        {sales.accordion.filter((row) => !extra || (row.text !== undefined && row.title !== "Descripción")).map((row) =>
          row.text !== undefined ? (
            <details key={row.title} name="servicio" className="group border-b border-(--wl-line)">
              <summary className={"flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 font-bold [&::-webkit-details-marker]:hidden " + FOCUS}>
                {row.title}
                <ChevronDown aria-hidden="true" className="size-5 shrink-0 transition-transform duration-300 group-open:rotate-180" />
              </summary>
              <p className="pb-4 text-[0.9375rem] leading-relaxed text-pretty">{row.text}</p>
            </details>
          ) : (
            <div key={row.title} className="flex min-h-14 items-center justify-between gap-4 border-b border-(--wl-line) font-bold">
              {row.title}
              <span className={PENDING}>{row.pending}</span>
            </div>
          ),
        )}
      </div>
    </>
  );
}

/**
 * La ficha de uno de los demás productos de la tienda (los de la ficha de datos, de 2 en adelante): el mismo
 * encabezado, precio, envío, botón, sellos y preguntas que el producto 1, sin sus packs, viñetas ni secciones.
 * Se compra de uno en uno (/checkout?producto=<id>).
 */
export function ExtraBuyArea({ content, id }: { content: ThemeContent; id: string }) {
  const sales = content.sales!;
  const item = content.catalog?.find((entry) => entry.id === id);
  if (!item) return null;
  return (
    <section id="comprar" aria-labelledby="producto-t" className="relative scroll-mt-20 overflow-x-clip pb-14 pt-8 lg:pt-14 lg:pb-24">
      <div aria-hidden="true" className="wl-blob top-0 -right-40 size-[28rem] bg-(--wl-accent) opacity-15" />
      <div className="relative mx-auto max-w-[75rem] px-5 lg:px-10">
        <div className="max-w-[40rem]">
          <h1 id="producto-t" className="font-display text-[2rem] leading-[1.05] tracking-[-0.015em] text-balance lg:text-[2.75rem] [font-weight:var(--wl-display-weight)] wl-display">
            {store.name}
            <span className="block text-[0.55em] leading-tight font-semibold tracking-normal text-(--wl-ink-muted) normal-case">{item.name}</span>
          </h1>
          <div className="mt-4">
            <span className="font-display text-[2rem] leading-none tabular-nums lg:text-[2.5rem] [font-weight:var(--wl-display-weight)]">{euros(item.price)}</span>
            <p className="mt-2 text-[0.8125rem] text-(--wl-ink-muted)">{sales.priceNote}</p>
          </div>
          {item.description ? <p className="mt-5 text-lg leading-snug font-semibold text-pretty">{item.description}</p> : null}
          <Link href={"/checkout?producto=" + item.id} data-buy-main="" className={PRIMARY + " mt-5 w-full"}>
            {sales.cta + " · " + euros(item.price)}
          </Link>
          <DeliveryLine content={content} />
          <Reassurance content={content} extra />
        </div>
      </div>
    </section>
  );
}

/** La ficha (5.3): galería y toda la compra. */
export function BuyArea({ content }: { content: ThemeContent }) {
  const sales = content.sales!;
  const action = useBuyAction(content);
  const hasPhotos = (content.media?.gallery ?? []).some((item) => item.photo !== undefined);
  return (
    <section id="comprar" aria-labelledby="producto-t" className="relative scroll-mt-20 overflow-x-clip pb-14 lg:pt-10 lg:pb-24">
      <div aria-hidden="true" className="wl-blob top-0 -right-40 size-[28rem] bg-(--wl-accent) opacity-15" />
      <div className="relative mx-auto grid max-w-[75rem] grid-cols-1 lg:grid-cols-12 lg:gap-12 lg:px-10">
        {hasPhotos ? (
          <div className="min-w-0 lg:col-span-7">
            <Gallery content={content} />
          </div>
        ) : null}
        <div className={"min-w-0 px-5 pt-5 lg:px-0 lg:pt-0 " + (hasPhotos ? "lg:col-span-5" : "lg:col-span-8 lg:col-start-3")}>
          <Rating content={content} />
          <h1 id="producto-t" className="mt-2 font-display text-[2rem] leading-[1.05] tracking-[-0.015em] text-balance lg:text-[2.75rem] [font-weight:var(--wl-display-weight)] wl-display">
            {store.name}
            <span className="block text-[0.55em] leading-tight font-semibold tracking-normal text-(--wl-ink-muted) normal-case">{sales.descriptor}</span>
          </h1>
          <div className="mt-4">
            <PriceRow content={content} price={action.price} units={action.units} />
          </div>
          <p className="mt-5 text-lg leading-snug font-semibold text-pretty">{sales.promise}</p>
          <ul className="mt-4 grid gap-2.5">
            {sales.bullets.slice(0, 5).map((bullet) => (
              <li key={bullet} className="flex items-start gap-2.5 font-medium">
                <span aria-hidden="true" className="wl-grad-accent mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-[0.625rem] font-black">✓</span>
                {bullet}
              </li>
            ))}
          </ul>
          <Choices content={content} />
          <Link href={action.link} onClick={action.onClick} data-buy-main="" className={PRIMARY + " mt-5 w-full"}>
            {action.label}
          </Link>
          <DeliveryLine content={content} />
          <Reassurance content={content} />
        </div>
      </div>
    </section>
  );
}

/** La caja corta de la portada (5.2, producto destacado): foto, precio, packs y botón. */
export function FeaturedBuy({ content }: { content: ThemeContent }) {
  const sales = content.sales!;
  const action = useBuyAction(content);
  const slot = content.media?.featured?.photo ? content.media.featured : undefined;
  return (
    <div className={"grid items-center gap-8 lg:gap-14 " + (slot ? "lg:grid-cols-2" : "mx-auto max-w-2xl")}>
      {slot ? <div className="relative">
        <div aria-hidden="true" className="wl-blob top-10 left-6 size-64 bg-(--wl-pair) opacity-40" />
        <div className="relative aspect-square overflow-hidden rounded-[28px] bg-(--wl-photo-bg) shadow-(--wl-shadow-soft)">
          {slot ? <SlotPhoto slot={slot} sizes="(min-width: 64rem) 40vw, 100vw" /> : null}
          <span className={BADGE + " absolute top-4 left-4"}><span aria-hidden="true">{sales.heroBadge.emoji}</span>{sales.heroBadge.text}</span>
        </div>
      </div> : null}
      <div className="rounded-[24px] bg-(--wl-surface) p-5 text-(--wl-ink) shadow-(--wl-shadow-soft) lg:p-8">
        <Rating content={content} />
        <p className="mt-2 font-display text-[1.75rem] leading-tight [font-weight:var(--wl-display-weight)] wl-display">{store.name + " · " + sales.descriptor}</p>
        <div className="mt-3">
          <PriceRow content={content} price={action.price} units={action.units} />
        </div>
        <Choices content={content} compact />
        <Link href={action.link} onClick={action.onClick} data-buy-cta="" className={PRIMARY + " mt-5 w-full"}>{action.label}</Link>
        <Link href={"/producto/" + sales.slug} className={"mt-3 flex min-h-11 items-center justify-center text-sm font-bold underline decoration-2 underline-offset-4 " + FOCUS}>Ver todos los detalles</Link>
      </div>
    </div>
  );
}

/** El botón principal suelto en una sección: mismo texto, añade lo elegido (07 §3). */
export function SectionBuy({ content, invert = false, className = "" }: { content: ThemeContent; invert?: boolean; className?: string }) {
  const action = useBuyAction(content);
  return (
    <Link href={action.link} onClick={action.onClick} data-buy-cta="" className={PRIMARY + (invert ? " wl-btn-invert" : "") + " w-full lg:w-auto lg:min-w-80 " + className}>
      {action.label}
    </Link>
  );
}

/**
 * C21: barra fija del móvil (72 px + zona segura), de cristal (7.2). Sale cuando el botón de la caja
 * no se ve y se oculta mientras se vea otro botón principal o el pie: nunca dos botones a la vez.
 */
export function StickyBar({ content }: { content: ThemeContent }) {
  const sales = content.sales!;
  const action = useBuyAction(content);
  const selection = useSelection(content);
  const [show, setShow] = useState(false);
  useEffect(() => {
    let frame = 0;
    const visible = (element: Element) => {
      const box = element.getBoundingClientRect();
      return box.bottom > 0 && box.top < window.innerHeight;
    };
    const update = () => {
      frame = 0;
      const main = document.querySelector("[data-buy-main]");
      const others = [...document.querySelectorAll("footer, [data-buy-cta]")];
      if (main === null) return setShow(false);
      const box = main.getBoundingClientRect();
      const far = box.bottom + window.scrollY > window.innerHeight * 1.5;
      const wanted = box.bottom < 0 || (far && box.top >= window.innerHeight);
      setShow(wanted && !others.some(visible));
    };
    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(update);
    };
    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, []);
  const pack = sales.packs[selection.pack];
  return (
    <div aria-hidden={!show} className={"wl-glass fixed inset-x-0 bottom-0 z-30 rounded-t-[22px] rounded-b-none border-b-0 pb-[env(safe-area-inset-bottom)] text-(--wl-ink) transition-transform duration-300 lg:hidden " + (show ? "translate-y-0" : "translate-y-[110%]")}>
      <div className="flex h-20 items-center gap-3 px-4">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[0.8125rem] font-semibold text-(--wl-ink-muted)">{pack ? (pack.units === 1 ? "1 unidad" : String(pack.units) + " unidades") : store.name}</p>
          <p className="text-lg leading-tight font-extrabold tabular-nums">{euros(action.price)}</p>
        </div>
        <Link href={action.link} onClick={action.onClick} tabIndex={show ? 0 : -1} className={PRIMARY + " min-h-[3.5rem] w-[62%] shrink-0 px-3 text-[0.9375rem]"}>
          {action.label.split(" · ")[0]}
        </Link>
      </div>
    </div>
  );
}
